import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Loader2, User, Sparkles, ChevronRight, Minus, Paperclip, Image, FileText, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useChat } from '@/context/ChatContext';
import { toast } from 'sonner';

// Renu Khosla's avatar - professional Indian woman
const PRIYA_AVATAR = "https://customer-assets.emergentagent.com/job_cfecca4a-b61c-4907-8d05-9794a419f91c/artifacts/kw229rsv_1000653411.jpg";

// Updated welcome message based on Master Chatbot Script
const WELCOME_MESSAGE = `Hello 👋 I'm your Workspace Planning Assistant. We help companies design efficient offices with factory-direct pricing on modular workstations, storage systems, and acoustic privacy booths.

I can help with layouts, budget estimates, product recommendations, and timelines.

What are you planning today?`;

// Automation Rules - Page-specific prompts
const PAGE_PROMPTS = {
  '/products': "Want a quick budget estimate for your workspace?",
  '/products/': "Would you like specs and pricing for this product?",
  '/contact': "Need help with your inquiry? I can assist!",
  '/bulk-inquiry': "Planning a bulk order? Let me help with the estimate."
};

// Idle prompt message
const IDLE_PROMPT = "Need help with pricing or layout planning? 🤔";

// Chat widget trigger delay (8 seconds as per settings)
const CHAT_TRIGGER_DELAY = 8000;

// Idle timeout (30 seconds as per automation rules)
const IDLE_TIMEOUT = 30000;

// Option sets for interactive buttons
const INITIAL_OPTIONS = [
  "New office setup",
  "Office expansion",
  "Upgrade / renovation",
  "Just exploring"
];

const EXPLORING_OPTIONS = [
  "View product catalog",
  "Get a Quote",
  "Understand pricing",
  "Explore solutions",
  "Talk to consultant"
];

const COMPANY_SIZE_OPTIONS = [
  "Under 20",
  "20–50",
  "50–100",
  "100+"
];

const REQUIREMENT_OPTIONS = [
  "Modular workstations",
  "Manager cabins",
  "Storage systems",
  "Acoustic booths",
  "Complete office setup"
];

const LAYOUT_OPTIONS = [
  "Yes, I can share it",
  "Need design support"
];

const BUDGET_OPTIONS = [
  "₹1–5 lakh",
  "₹5–10 lakh",
  "₹10–25 lakh",
  "₹25–50 lakh",
  "₹50 lakh+",
  "Need guidance"
];

const TIMELINE_OPTIONS = [
  "Immediate",
  "1–3 months",
  "3–6 months",
  "Planning stage"
];

const ROLE_OPTIONS = [
  "Founder / Owner",
  "Procurement",
  "Admin",
  "Architect / Interior Designer"
];

const FOLLOW_UP_OPTIONS = [
  "Budget estimate",
  "Layout proposal",
  "Schedule consultation"
];

export const AIChatWidget = ({ isOpen: externalIsOpen, onClose: externalOnClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isChatOpen, setIsChatOpen } = useChat();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadInfo, setLeadInfo] = useState({ name: '', email: '', phone: '', company: '' });
  const [currentOptions, setCurrentOptions] = useState([]);
  const [qualificationStep, setQualificationStep] = useState(0);
  const [showAutoPrompt, setShowAutoPrompt] = useState(false);
  const [autoPromptMessage, setAutoPromptMessage] = useState('');
  const [hasShownPagePrompt, setHasShownPagePrompt] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showEmailSummary, setShowEmailSummary] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const idleTimerRef = useRef(null);
  const triggerTimerRef = useRef(null);

  // Handle external open state
  useEffect(() => {
    if (externalIsOpen) {
      setIsOpen(true);
      setIsMinimized(false);
    }
  }, [externalIsOpen]);

  // Notify external onClose when closing
  const handleClose = () => {
    setIsOpen(false);
    setIsMinimized(false);
    if (externalOnClose) {
      externalOnClose();
    }
  };

  // Sync local isOpen state with global context
  useEffect(() => {
    setIsChatOpen(isOpen && !isMinimized);
  }, [isOpen, isMinimized, setIsChatOpen]);

  // Listen for external triggers to open chat (e.g., from MobileFABMenu)
  useEffect(() => {
    if (isChatOpen && !isOpen) {
      setIsOpen(true);
      setIsMinimized(false);
    }
  }, [isChatOpen, isOpen]);

  // Auto-trigger chat button pulse after 8 seconds
  useEffect(() => {
    const hasSeenChat = sessionStorage.getItem('mfs_chat_seen');
    if (!hasSeenChat && !isOpen) {
      triggerTimerRef.current = setTimeout(() => {
        setShowAutoPrompt(true);
        setAutoPromptMessage("👋 Need help planning your office?");
        sessionStorage.setItem('mfs_chat_seen', 'true');
        // Auto-hide after 5 seconds
        setTimeout(() => setShowAutoPrompt(false), 5000);
      }, CHAT_TRIGGER_DELAY);
    }
    return () => {
      if (triggerTimerRef.current) clearTimeout(triggerTimerRef.current);
    };
  }, [isOpen]);

  // Page-specific prompts
  useEffect(() => {
    if (!isOpen && !hasShownPagePrompt) {
      const currentPath = location.pathname;
      
      // Check for exact matches first
      if (PAGE_PROMPTS[currentPath]) {
        setTimeout(() => {
          setShowAutoPrompt(true);
          setAutoPromptMessage(PAGE_PROMPTS[currentPath]);
          setHasShownPagePrompt(true);
          setTimeout(() => setShowAutoPrompt(false), 5000);
        }, 3000);
      }
      // Check for product detail page
      else if (currentPath.startsWith('/product/') || currentPath.match(/^\/products\/\d+/)) {
        setTimeout(() => {
          setShowAutoPrompt(true);
          setAutoPromptMessage("Would you like specs and pricing for this product?");
          setHasShownPagePrompt(true);
          setTimeout(() => setShowAutoPrompt(false), 5000);
        }, 3000);
      }
    }
  }, [location.pathname, isOpen, hasShownPagePrompt]);

  // Idle detection - show prompt after 30 seconds of inactivity
  useEffect(() => {
    if (isOpen && !isMinimized && messages.length > 0) {
      // Reset idle timer on any activity
      const resetIdleTimer = () => {
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
        idleTimerRef.current = setTimeout(() => {
          // Only show if there's been no recent message
          const lastMsg = messages[messages.length - 1];
          if (lastMsg && lastMsg.role === 'assistant' && !isLoading) {
            setMessages(prev => {
              // Don't add if the last message is already the idle prompt
              if (prev[prev.length - 1]?.content === IDLE_PROMPT) return prev;
              return [...prev, { role: 'assistant', content: IDLE_PROMPT }];
            });
          }
        }, IDLE_TIMEOUT);
      };
      
      resetIdleTimer();
      
      // Listen for user activity
      window.addEventListener('mousemove', resetIdleTimer);
      window.addEventListener('keypress', resetIdleTimer);
      
      return () => {
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
        window.removeEventListener('mousemove', resetIdleTimer);
        window.removeEventListener('keypress', resetIdleTimer);
      };
    }
  }, [isOpen, isMinimized, messages.length, isLoading]);

  // Detect which options to show based on last message
  const detectOptions = (message) => {
    const lowerMsg = message.toLowerCase();
    
    // Check for specific question patterns
    if (lowerMsg.includes('what are you planning today') || lowerMsg.includes('planning today')) {
      return INITIAL_OPTIONS;
    }
    if (lowerMsg.includes('happy to help') && lowerMsg.includes('would you like')) {
      return EXPLORING_OPTIONS;
    }
    if (lowerMsg.includes('how many employees') || lowerMsg.includes('workspace accommodate')) {
      return COMPANY_SIZE_OPTIONS;
    }
    if (lowerMsg.includes('what solutions are you looking') || lowerMsg.includes('looking for')) {
      return REQUIREMENT_OPTIONS;
    }
    if (lowerMsg.includes('floor plan available') || lowerMsg.includes('have a floor plan')) {
      return LAYOUT_OPTIONS;
    }
    if (lowerMsg.includes('approximate budget') || lowerMsg.includes('your budget')) {
      return BUDGET_OPTIONS;
    }
    if (lowerMsg.includes('expected timeline') || lowerMsg.includes('timeline')) {
      return TIMELINE_OPTIONS;
    }
    if (lowerMsg.includes('your role') || lowerMsg.includes('role in this project')) {
      return ROLE_OPTIONS;
    }
    if (lowerMsg.includes('meanwhile would you like') || lowerMsg.includes('would you like:')) {
      if (lowerMsg.includes('budget estimate') || lowerMsg.includes('layout proposal')) {
        return FOLLOW_UP_OPTIONS;
      }
    }
    
    return [];
  };

  // Generate session ID on mount
  useEffect(() => {
    const storedSessionId = localStorage.getItem('mfs_chat_session');
    if (storedSessionId) {
      setSessionId(storedSessionId);
      loadChatHistory(storedSessionId);
    } else {
      const newSessionId = `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setSessionId(newSessionId);
      localStorage.setItem('mfs_chat_session', newSessionId);
      // Add welcome message
      setMessages([{
        role: 'assistant',
        content: WELCOME_MESSAGE
      }]);
      setCurrentOptions(INITIAL_OPTIONS);
    }
  }, []);

  // Load chat history
  const loadChatHistory = async (sid) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/chat/history/${sid}`);
      const data = await response.json();
      if (data.messages && data.messages.length > 0) {
        setMessages(data.messages);
        // Detect options from last assistant message
        const lastAssistantMsg = [...data.messages].reverse().find(m => m.role === 'assistant');
        if (lastAssistantMsg) {
          setCurrentOptions(detectOptions(lastAssistantMsg.content));
        }
      } else {
        setMessages([{
          role: 'assistant',
          content: WELCOME_MESSAGE
        }]);
        setCurrentOptions(INITIAL_OPTIONS);
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
      setMessages([{
        role: 'assistant',
        content: WELCOME_MESSAGE
      }]);
      setCurrentOptions(INITIAL_OPTIONS);
    }
  };

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const sendMessage = async (messageOverride = null) => {
    const userMessage = messageOverride || inputValue.trim();
    if (!userMessage || isLoading) return;

    // Check for quotation-related keywords
    const quotationKeywords = ['quote', 'quotation', 'pricing', 'price list', 'cost estimate', 'estimate', 'how much'];
    const isQuotationRequest = quotationKeywords.some(keyword => 
      userMessage.toLowerCase().includes(keyword)
    );

    if (isQuotationRequest) {
      setInputValue('');
      setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
      
      // Show quotation guidance
      const quoteResponse = {
        role: 'assistant',
        content: "To provide you with an accurate quotation, please select the products you're interested in first.\n\n👉 **Select Product to Get Quote**\n\nOnce you've selected your products, I can help you with:\n• Quantity-based pricing\n• Bulk discounts\n• Delivery estimates\n• Custom configurations",
        options: ["Select Product to Get Quote"]
      };
      setMessages(prev => [...prev, quoteResponse]);
      setCurrentOptions(["Select Product to Get Quote"]);
      return;
    }

    setInputValue('');
    setCurrentOptions([]); // Clear options when sending
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId,
          user_name: leadInfo.name || null,
          user_email: leadInfo.email || null,
          user_phone: leadInfo.phone || null
        })
      });

      const data = await response.json();
      const aiResponse = data.response;
      setMessages(prev => [...prev, { role: 'assistant', content: aiResponse }]);
      
      // Detect and set options for the new response
      const newOptions = detectOptions(aiResponse);
      setCurrentOptions(newOptions);

      // Check if AI is asking for lead details
      if (aiResponse.toLowerCase().includes('may i have your details') || 
          aiResponse.toLowerCase().includes('your details') ||
          aiResponse.toLowerCase().includes('name') && aiResponse.toLowerCase().includes('phone')) {
        setTimeout(() => setShowLeadForm(true), 1000);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm having trouble connecting. Please try again or call us at +91 9594685818." 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleOptionClick = (option) => {
    // Handle special navigation options
    if (option === "View product catalog") {
      navigate('/products');
      setIsOpen(false);
      return;
    }
    if (option === "Get a Quote" || option === "Budget estimate") {
      // Show quotation guidance message
      const quoteMessage = {
        role: 'assistant',
        content: "To provide you with an accurate quotation, please select the products you're interested in first.\n\n**[Select Product to Get Quote →](select-product)**\n\nOnce you've selected your products, I can help you with:\n• Quantity-based pricing\n• Bulk discounts\n• Delivery estimates\n• Custom configurations",
        options: ["Select Product to Get Quote"]
      };
      setMessages(prev => [...prev, { role: 'user', content: option }, quoteMessage]);
      setCurrentOptions(["Select Product to Get Quote"]);
      return;
    }
    if (option === "Select Product to Get Quote") {
      navigate('/products');
      setIsOpen(false);
      toast.success("Select products and add to cart for quotation");
      return;
    }
    if (option === "Talk to consultant" || option === "Schedule consultation") {
      navigate('/contact');
      setIsOpen(false);
      return;
    }
    // Send the option as a message
    sendMessage(option);
  };

  const submitLeadForm = async () => {
    if (!leadInfo.name || !leadInfo.phone) return;

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API_URL}/api/chat/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...leadInfo,
          session_id: sessionId
        })
      });

      setShowLeadForm(false);
      
      // Send a message to AI with the captured info
      const leadMessage = `My name is ${leadInfo.name}, phone: ${leadInfo.phone}${leadInfo.email ? ', email: ' + leadInfo.email : ''}${leadInfo.company ? ', company: ' + leadInfo.company : ''}`;
      sendMessage(leadMessage);
    } catch (error) {
      console.error('Lead capture error:', error);
    }
  };

  // Handle file attachment
  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setIsUploading(true);
    const newFiles = [];

    for (const file of files) {
      // Validate file type (images and PDFs)
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}: Only images (JPG, PNG, WebP) and PDF files are allowed`);
        continue;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: File size must be under 10MB`);
        continue;
      }

      // Convert to base64 for preview and upload
      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });

      newFiles.push({
        name: file.name,
        type: file.type,
        size: file.size,
        base64: base64,
        preview: file.type.startsWith('image/') ? base64 : null
      });
    }

    setAttachedFiles(prev => [...prev, ...newFiles]);
    setIsUploading(false);

    // Add message about attached files
    if (newFiles.length > 0) {
      const fileNames = newFiles.map(f => f.name).join(', ');
      setMessages(prev => [...prev, {
        role: 'user',
        content: `📎 Attached: ${fileNames}`,
        isAttachment: true,
        files: newFiles
      }]);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove attached file
  const removeFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Send chat summary via email
  const sendEmailSummary = async () => {
    if (!leadInfo.name || !leadInfo.phone) {
      toast.error('Please provide your name and phone number first');
      setShowLeadForm(true);
      return;
    }

    setIsSendingEmail(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Prepare chat summary
      const chatSummary = messages
        .filter(m => !m.isAttachment)
        .map(m => `${m.role === 'user' ? 'Guest' : 'Renu Khosla'}: ${m.content}`)
        .join('\n\n');

      const response = await fetch(`${API_URL}/api/chat/email-summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          guest_name: leadInfo.name,
          guest_email: leadInfo.email,
          guest_phone: leadInfo.phone,
          guest_company: leadInfo.company,
          chat_summary: chatSummary,
          attached_files: attachedFiles.map(f => ({
            name: f.name,
            type: f.type,
            base64: f.base64
          }))
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Chat summary sent to our team! We\'ll contact you shortly.');
        setShowEmailSummary(false);
      } else {
        toast.error('Failed to send email. Please try again.');
      }
    } catch (error) {
      console.error('Email error:', error);
      toast.error('Failed to send email. Please try again.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Start new conversation
  const startNewChat = () => {
    localStorage.removeItem('mfs_chat_session');
    const newSessionId = `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newSessionId);
    localStorage.setItem('mfs_chat_session', newSessionId);
    setMessages([{
      role: 'assistant',
      content: WELCOME_MESSAGE
    }]);
    setCurrentOptions(INITIAL_OPTIONS);
    setShowLeadForm(false);
    setLeadInfo({ name: '', email: '', phone: '', company: '' });
    setAttachedFiles([]);
  };

  return (
    <>
      {/* Chat Window - show when open AND not minimized */}
      {isOpen && !isMinimized && (
        <div 
          className="fixed right-4 sm:right-6 bottom-4 sm:bottom-6 z-[60] w-[calc(100vw-32px)] sm:w-[380px] h-[70vh] sm:h-[500px] max-h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <img 
                  src={PRIYA_AVATAR} 
                  alt="Renu Khosla" 
                  className="w-10 h-10 rounded-full object-cover border-2 border-[#c5a059]"
                />
                <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-[#1a1a1a]"></span>
              </div>
              <div>
                <h3 className="font-bold text-sm">Renu Khosla</h3>
                <p className="text-xs text-[#c5a059]">Workspace Planning Assistant</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button 
                onClick={startNewChat}
                className="text-[10px] px-2 py-1 bg-white/10 hover:bg-white/20 rounded-md transition-colors"
                title="Start new conversation"
              >
                New Chat
              </button>
              <button 
                onClick={() => setIsMinimized(true)}
                className="p-1.5 hover:bg-white/10 rounded-full transition-colors"
                title="Minimize"
              >
                <Minus className="w-4 h-4" />
              </button>
              <button 
                onClick={handleClose}
                className="p-1.5 hover:bg-white/10 rounded-full transition-colors"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((msg, idx) => (
              <div 
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex items-start gap-2 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  {msg.role === 'assistant' ? (
                    <img 
                      src={PRIYA_AVATAR} 
                      alt="Renu Khosla" 
                      className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#c5a059] flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <div 
                    className={`rounded-2xl px-4 py-2.5 text-sm ${
                      msg.role === 'user' 
                        ? 'bg-[#c5a059] text-[#1a1a1a] rounded-tr-sm' 
                        : 'bg-white shadow-sm border border-gray-100 rounded-tl-sm'
                    }`}
                  >
                    {msg.content}
                  </div>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex items-start gap-2">
                <img 
                  src={PRIYA_AVATAR} 
                  alt="Renu Khosla" 
                  className="w-8 h-8 rounded-full object-cover"
                />
                <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm border border-gray-100">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></span>
                  </div>
                </div>
              </div>
            )}

            {/* Lead Form */}
            {showLeadForm && !leadInfo.name && (
              <div className="bg-gradient-to-br from-[#c5a059]/10 to-[#c5a059]/5 rounded-xl p-4 border border-[#c5a059]/30">
                <p className="text-sm font-semibold text-[#1a1a1a] mb-3">
                  📋 Before I share recommendations, may I have your details?
                </p>
                <div className="space-y-2">
                  <Input
                    placeholder="Your Name *"
                    value={leadInfo.name}
                    onChange={(e) => setLeadInfo({...leadInfo, name: e.target.value})}
                    className="text-sm h-9"
                  />
                  <Input
                    placeholder="Company Name"
                    value={leadInfo.company}
                    onChange={(e) => setLeadInfo({...leadInfo, company: e.target.value})}
                    className="text-sm h-9"
                  />
                  <Input
                    placeholder="Phone Number *"
                    value={leadInfo.phone}
                    onChange={(e) => setLeadInfo({...leadInfo, phone: e.target.value})}
                    className="text-sm h-9"
                  />
                  <Input
                    placeholder="Email (optional)"
                    value={leadInfo.email}
                    onChange={(e) => setLeadInfo({...leadInfo, email: e.target.value})}
                    className="text-sm h-9"
                  />
                  {/* Consent Message */}
                  <p className="text-[10px] text-gray-500 px-1">
                    By continuing, you agree to be contacted regarding your workspace inquiry.
                  </p>
                  <div className="flex gap-2">
                    <Button 
                      onClick={submitLeadForm}
                      className="flex-1 h-9 bg-[#c5a059] hover:bg-[#b8934d] text-[#1a1a1a] text-xs font-semibold"
                      disabled={!leadInfo.name || !leadInfo.phone}
                    >
                      Submit
                    </Button>
                    <Button 
                      variant="ghost"
                      onClick={() => setShowLeadForm(false)}
                      className="h-9 text-xs"
                    >
                      Skip
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Interactive Options */}
          {currentOptions.length > 0 && !isLoading && (
            <div className="px-3 pb-2 bg-gray-50 border-t border-gray-100">
              <div className="flex flex-wrap gap-2 pt-2">
                {currentOptions.map((option, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleOptionClick(option)}
                    className="text-xs px-3 py-2 rounded-lg border border-[#c5a059] text-[#1a1a1a] bg-white hover:bg-[#c5a059] hover:text-[#1a1a1a] transition-all duration-200 flex items-center gap-1 shadow-sm"
                  >
                    {option}
                    <ChevronRight className="w-3 h-3 opacity-50" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-3 border-t border-gray-200 bg-white">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="flex-1 text-sm rounded-full border-gray-200 focus:border-[#c5a059] focus:ring-[#c5a059]"
                disabled={isLoading}
              />
              <Button
                onClick={() => sendMessage()}
                disabled={!inputValue.trim() || isLoading}
                className="rounded-full w-10 h-10 p-0 bg-[#c5a059] hover:bg-[#b8934d] disabled:opacity-50"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin text-[#1a1a1a]" />
                ) : (
                  <Send className="w-4 h-4 text-[#1a1a1a]" />
                )}
              </Button>
            </div>

            {/* Attached Files Preview */}
            {attachedFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {attachedFiles.map((file, idx) => (
                  <div key={idx} className="flex items-center gap-1 bg-gray-100 rounded-full px-2 py-1 text-xs">
                    {file.type.startsWith('image/') ? (
                      <Image className="w-3 h-3 text-blue-500" />
                    ) : (
                      <FileText className="w-3 h-3 text-red-500" />
                    )}
                    <span className="max-w-[80px] truncate">{file.name}</span>
                    <button 
                      onClick={() => removeFile(idx)}
                      className="text-gray-400 hover:text-red-500 ml-1"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-2">
                {/* File Attachment Button */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-[#c5a059] transition-colors"
                  title="Attach floor plan or image"
                >
                  {isUploading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Paperclip className="w-4 h-4" />
                  )}
                  <span className="hidden sm:inline">Attach File</span>
                </button>

                {/* Email Summary Button */}
                <button
                  onClick={sendEmailSummary}
                  disabled={isSendingEmail || messages.length < 3}
                  className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-[#c5a059] transition-colors disabled:opacity-50"
                  title="Email chat summary to our team"
                >
                  {isSendingEmail ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4" />
                  )}
                  <span className="hidden sm:inline">Email Summary</span>
                </button>
              </div>

              <p className="text-[10px] text-gray-400">
                Powered by AI
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
